import { useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'

type PayableUploadDialogProps = {
  open: boolean
  loading?: boolean
  onClose: () => void
  onUpload: (file: File) => Promise<void>
}

const PayableUploadDialog = ({
  open,
  loading,
  onClose,
  onUpload,
}: PayableUploadDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleClose = () => {
    setFile(null)
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a file')
      return
    }
    try {
      setError(null)
      await onUpload(file)
      setFile(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Upload vendor invoice</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload a receipt or vendor invoice (JPEG, PNG, or PDF).
          </Typography>
          <Button variant="outlined" onClick={() => inputRef.current?.click()}>
            {file ? file.name : 'Choose file'}
          </Button>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".pdf,.png,.jpg,.jpeg,image/*,application/pdf"
            onChange={(e) => {
              const selected = e.target.files?.[0] ?? null
              setFile(selected)
              setError(null)
            }}
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <SubmitLockButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !file}
        >
          {loading ? 'Uploading…' : 'Upload'}
        </SubmitLockButton>
      </DialogActions>
    </Dialog>
  )
}

export default PayableUploadDialog
